import React, { useState, useRef, useEffect } from "react";
import { useViewSize } from '@/providers/ViewSizeContext';
import { useClientMount } from '@/providers/ClientMountProvider';
import FooterColumn from "./FooterColumn";

import classNames from "classnames/bind";
import styles from "./FooterColumns.module.scss";
const cx = classNames.bind(styles);

type FooterColumns = FooterSettings["footerSettingsFields"]["footerColumns"];

// Props for the FooterColumns component
type FooterColumnsProps = {
  footerColumns: FooterColumns;
};

const FooterColumns: React.FC<FooterColumnsProps> = ({ footerColumns }) => {
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // Use explicit collapsible mode check: anything under desktop breakpoint (< 1025px)
  const isCollapsibleMode = !isDesktop;

  // To allow multiple columns to be expanded, use an array of indices
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousIsCollapsibleRef = useRef<boolean | null>(null);

  // Reset expanded state when switching between mobile and desktop
  useEffect(() => {
    if (!mounted || !isReady) return;

    // Detect if collapsible mode changed (viewport mode switched)
    const modeChanged = previousIsCollapsibleRef.current !== null && previousIsCollapsibleRef.current !== isCollapsibleMode;
    previousIsCollapsibleRef.current = isCollapsibleMode;

    // Clear any pending timeout
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // If viewport mode changed, clear immediately to prevent broken state
    // Otherwise use a longer delay for resize stability
    if (modeChanged) {
      setExpandedIndices([]);
    } else {
      resizeTimeoutRef.current = setTimeout(() => {
        setExpandedIndices([]);
      }, 200);
    }

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [isCollapsibleMode, mounted, isReady]);

  // To revert to single expansion, replace expandedIndices with expandedIndex: number | null
  const handleHeaderClick = (index: number) => {
    if (!isCollapsibleMode) return;
    setExpandedIndices(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  if (!footerColumns || !footerColumns.length) return null;

  // -------------------- Render

  return (
    <div className={cx("footer-columns")}>
      {footerColumns.map((column, index) => (
        <FooterColumn
          {...column}
          key={index}
          index={index}
          isLast={index === footerColumns.length - 1}
          handleHeaderClick={handleHeaderClick}
          activeIndices={expandedIndices}
        />
      ))}
    </div>
  );
};

export default FooterColumns;
